import dayjs from 'dayjs';
import { AppState } from 'src/store/store';
import { TokenListItem, TokensState } from './types';
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import tokensApiService from 'src/api/services/tokensApiService';
import { notification } from 'antd';

const initialState: TokensState = {
    isLoading: false,
    items: [],
    requestId: undefined
};

export const loadTokensAsync = createAsyncThunk('tokens/load', async () => {
    try {
        const data = await tokensApiService.getTokens();
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
        }
        throw new Error();
    }
});

export const lockToken = createAsyncThunk('token/lock', async (token: TokenListItem) => {
    try {
        await tokensApiService.lockToken(token.name);
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
        }
        throw new Error();
    }
});

export const unlockToken = createAsyncThunk('token/unlock', async (token: TokenListItem) => {
    try {
        await tokensApiService.unlockToken(token.name);
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
        }
        throw new Error();
    }
});

const slice = createSlice({
    name: 'tokens',
    initialState,
    reducers: {
        reset: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadTokensAsync.pending, (state) => {
                state.isLoading = true;
                state.items = [];
            })
            .addCase(loadTokensAsync.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload.map((dto, index: number) => {
                    const item: TokenListItem = {
                        ...dto,
                        dateStart: dto.dateStart
                            ? dayjs(`${dto.dateStart}`).format('DD.MM.YYYY')
                            : dayjs().format('DD.MM.YYYY'),
                        dateEnd: dto.dateEnd ? dayjs(`${dto.dateEnd}`).format('DD.MM.YYYY') : '',
                        key: `${dto.name}-${index}`
                    };
                    return item;
                });
            })
            .addCase(loadTokensAsync.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(lockToken.pending, (state, action) => {
                state.isLoading = true;
                state.requestId = action.meta.requestId;
            })
            .addCase(lockToken.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    const item = state.items.find((item) => item.key === action.meta.arg.key);
                    if (item) {
                        item.isActive = false;
                    }
                }
            })
            .addCase(lockToken.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            })
            .addCase(unlockToken.pending, (state, action) => {
                state.isLoading = true;
                state.requestId = action.meta.requestId;
            })
            .addCase(unlockToken.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    const item = state.items.find((item) => item.key === action.meta.arg.key);
                    if (item) {
                        item.isActive = true;
                    }
                }
            })
            .addCase(unlockToken.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            });
    }
});

const selectTokens = (state: AppState) => state.tokens;

export const selectLoading = (state: AppState) => selectTokens(state).isLoading;

export const selectItems = (state: AppState) => selectTokens(state).items;

export const selectOrderedItems = createSelector(selectItems, (items) =>
    [...items].sort((token1, token2) => (token1.dateStart < token2.dateStart ? 1 : -1))
);

export const selectTotalCount = createSelector(selectItems, (items) => items.length);

export const { reset } = slice.actions;

export default slice.reducer;
